import React from "react";

const FONT = "Inter, ui-sans-serif, system-ui, sans-serif";
const DISPLAY = "'Space Grotesk', Inter, sans-serif";

const Frame = ({ children, label, testid }) => (
  <svg viewBox="0 0 480 400" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" role="img" aria-label={label} data-testid={testid}>
    <rect width="480" height="400" fill="#FFFFFF" />
    {children}
  </svg>
);

const Card = ({ x, y, w, h, children }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width={w} height={h} rx="14" fill="#FFFFFF" stroke="#E6E6E0" />
    {children}
  </g>
);

const Label = ({ x, y, children, color = "#6B6B73" }) => (
  <text x={x} y={y} fontFamily={FONT} fontSize="9" fontWeight="600" fill={color} letterSpacing="1.2">
    {children}
  </text>
);

/* Driver safety: gauge + ranked drivers */
export const DriverScoreVisual = () => {
  const drivers = [
    ["Omar K.", 94, "#16A34A"],
    ["Ravi S.", 88, "#16A34A"],
    ["Ahmed M.", 76, "#F59E0B"],
    ["Yusuf A.", 61, "#E5484D"],
  ];
  return (
    <Frame label="Driver safety score and ranking" testid="visual-driver-score">
      <Card x={24} y={24} w={200} h={352}>
        <Label x={18} y={28}>FLEET SAFETY SCORE</Label>
        <circle cx="100" cy="150" r="70" fill="none" stroke="#EDEAFF" strokeWidth="14" />
        <circle cx="100" cy="150" r="70" fill="none" stroke="#4C30FF" strokeWidth="14" strokeLinecap="round" strokeDasharray="440" strokeDashoffset="440" transform="rotate(-90 100 150)">
          <animate attributeName="stroke-dashoffset" from="440" to="66" dur="1.6s" fill="freeze" calcMode="spline" keySplines="0.2 0.8 0.2 1" />
        </circle>
        <text x="100" y="158" textAnchor="middle" fontFamily={DISPLAY} fontSize="40" fontWeight="700" fill="#0E0E10">
          85
        </text>
        <text x="100" y="178" textAnchor="middle" fontFamily={FONT} fontSize="10" fill="#6B6B73">
          out of 100 · this month
        </text>
        <g transform="translate(18 250)">
          {[
            ["Speeding", 12, "#E5484D"],
            ["Harsh braking", 7, "#F59E0B"],
            ["Idling > 10 min", 19, "#4C30FF"],
          ].map(([n, v, c], i) => (
            <g key={n} transform={`translate(0 ${i * 32})`}>
              <text fontFamily={FONT} fontSize="10" fill="#2A2A30" y="10">
                {n}
              </text>
              <text x="164" y="10" textAnchor="end" fontFamily={FONT} fontSize="10" fontWeight="600" fill={c}>
                {`${v} events`}
              </text>
              <rect y="16" width="164" height="4" rx="2" fill="#F4F4F0" />
              <rect y="16" width="0" height="4" rx="2" fill={c}>
                <animate attributeName="width" from="0" to={v * 7} dur="1.2s" begin={`${0.3 + i * 0.15}s`} fill="freeze" />
              </rect>
            </g>
          ))}
        </g>
      </Card>
      <Card x={240} y={24} w={216} h={352}>
        <Label x={18} y={28}>DRIVER RANKING</Label>
        {drivers.map(([n, s, c], i) => (
          <g key={n} transform={`translate(18 ${52 + i * 66})`}>
            <circle cx="14" cy="14" r="14" fill="#EDEAFF" />
            <text x="14" y="18" textAnchor="middle" fontFamily={FONT} fontSize="10" fontWeight="700" fill="#3A1FE0">
              {n[0]}
            </text>
            <text x="38" y="12" fontFamily={FONT} fontSize="11" fontWeight="600" fill="#0E0E10">
              {n}
            </text>
            <text x="38" y="26" fontFamily={FONT} fontSize="9" fill="#6B6B73">
              {["Truck · TRK-204", "Van · VAN-17", "Car · CAR-09", "Truck · TRK-11"][i]}
            </text>
            <text x="180" y="18" textAnchor="end" fontFamily={DISPLAY} fontSize="18" fontWeight="700" fill={c}>
              {s}
            </text>
            <rect y="40" width="180" height="4" rx="2" fill="#F4F4F0" />
            <rect y="40" width="0" height="4" rx="2" fill={c}>
              <animate attributeName="width" from="0" to={s * 1.8} dur="1.2s" begin={`${0.2 + i * 0.12}s`} fill="freeze" />
            </rect>
          </g>
        ))}
        <g transform="translate(18 326)">
          <rect width="180" height="30" rx="15" fill="#FEF2F2" stroke="#FECACA" />
          <circle cx="16" cy="15" r="4" fill="#E5484D">
            <animate attributeName="opacity" values="1;0.3;1" dur="1.2s" repeatCount="indefinite" />
          </circle>
          <text x="28" y="19" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill="#B91C1C">
            Harsh braking · Yusuf A. · just now
          </text>
        </g>
      </Card>
    </Frame>
  );
};

/* Fuel & engine: fuel level line with refuel / drop events + engine tiles */
export const FuelVisual = () => (
  <Frame label="Fuel level over a day with refuel and drop events" testid="visual-fuel">
    <Card x={24} y={24} w={432} h={214}>
      <Label x={18} y={28}>FUEL LEVEL · TRK-204 · TODAY</Label>
      <text x="414" y="28" textAnchor="end" fontFamily={FONT} fontSize="9" fontWeight="600" fill="#16A34A">
        -11% vs last week
      </text>
      {[60, 100, 140, 180].map((y) => (
        <line key={y} x1="18" x2="414" y1={y} y2={y} stroke="#F1F1EC" />
      ))}
      <path d="M18 110 L80 118 L120 128 L150 60 L210 72 L260 86 L300 96 L320 150 L360 160 L414 172" fill="none" stroke="#4C30FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="600" strokeDashoffset="600">
        <animate attributeName="stroke-dashoffset" from="600" to="0" dur="2s" fill="freeze" />
      </path>
      <path d="M18 110 L80 118 L120 128 L150 60 L210 72 L260 86 L300 96 L320 150 L360 160 L414 172 V 190 H 18 Z" fill="#4C30FF" opacity="0.06" />
      <g transform="translate(150 60)">
        <circle r="5" fill="#16A34A" />
        <rect x="-34" y="-30" width="68" height="18" rx="9" fill="#16A34A" />
        <text y="-18" textAnchor="middle" fontFamily={FONT} fontSize="8.5" fontWeight="600" fill="#FFF">
          Refuel +180 L
        </text>
      </g>
      <g transform="translate(320 150)">
        <circle r="5" fill="#E5484D">
          <animate attributeName="r" values="5;8;5" dur="1.4s" repeatCount="indefinite" />
        </circle>
        <rect x="-6" y="12" width="92" height="18" rx="9" fill="#E5484D" />
        <text x="40" y="24" textAnchor="middle" fontFamily={FONT} fontSize="8.5" fontWeight="600" fill="#FFF">
          Fuel drop -42 L
        </text>
      </g>
      {["06:00", "09:00", "12:00", "15:00", "18:00"].map((t, i) => (
        <text key={t} x={18 + i * 99} y="204" fontFamily={FONT} fontSize="8.5" fill="#9A9AA8">
          {t}
        </text>
      ))}
    </Card>
    {[
      ["ENGINE HOURS", "1,284 h", "#0E0E10"],
      ["ODOMETER", "182,410 km", "#0E0E10"],
      ["FAULT CODES", "P0401 · EGR", "#E5484D"],
    ].map(([l, v, c], i) => (
      <Card key={l} x={24 + i * 146} y={254} w={140} h={122}>
        <Label x={16} y={26}>{l}</Label>
        <text x="16" y="66" fontFamily={DISPLAY} fontSize={i === 2 ? "15" : "20"} fontWeight="700" fill={c}>
          {v}
        </text>
        <text x="16" y="88" fontFamily={FONT} fontSize="9" fill="#6B6B73">
          {["synced from CAN-bus", "synced from CAN-bus", "service recommended"][i]}
        </text>
        <rect x="16" y="100" width="108" height="4" rx="2" fill="#F4F4F0" />
        <rect x="16" y="100" width="0" height="4" rx="2" fill={c}>
          <animate attributeName="width" from="0" to={[72, 90, 40][i]} dur="1.2s" begin={`${0.4 + i * 0.15}s`} fill="freeze" />
        </rect>
      </Card>
    ))}
  </Frame>
);

/* Cold chain: zones with temperature + event log */
export const ColdChainVisual = () => (
  <Frame label="Cold chain temperature zones and event log" testid="visual-cold-chain">
    {[
      ["REEFER · ZONE A", "-18.4°C", "Within range", "#16A34A", 0],
      ["REEFER · ZONE B", "4.2°C", "Within range", "#16A34A", 1],
      ["CARGO BOX · VAN-17", "9.4°C", "Limit 8°C breached", "#E5484D", 2],
    ].map(([n, t, s, c, i]) => (
      <Card key={n} x={24 + i * 146} y={24} w={140} h={150}>
        <Label x={16} y={26}>{n}</Label>
        <text x="16" y="74" fontFamily={DISPLAY} fontSize="28" fontWeight="700" fill="#0E0E10">
          {t}
        </text>
        <circle cx="22" cy="104" r="4" fill={c}>
          {c === "#E5484D" && <animate attributeName="opacity" values="1;0.3;1" dur="1.1s" repeatCount="indefinite" />}
        </circle>
        <text x="32" y="108" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill={c}>
          {s}
        </text>
        <path d={["M16 132 L40 128 L64 130 L88 126 L112 129 L124 127", "M16 130 L40 131 L64 128 L88 130 L112 129 L124 130", "M16 136 L40 134 L64 130 L88 124 L112 118 L124 112"][i]} fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeDasharray="140" strokeDashoffset="140">
          <animate attributeName="stroke-dashoffset" from="140" to="0" dur="1.6s" begin={`${i * 0.2}s`} fill="freeze" />
        </path>
      </Card>
    ))}
    <Card x={24} y={190} w={432} h={186}>
      <Label x={18} y={28}>EVENT LOG · LIVE</Label>
      {[
        ["Temperature alert · VAN-17 · 9.4°C", "10:52 · Sheikh Zayed Rd", "#E5484D", "Alert"],
        ["Door opened · TRK-204", "10:41 · Al Quoz depot", "#F59E0B", "Logged"],
        ["Pre-cooling complete · Zone A", "09:30 · Jebel Ali", "#16A34A", "OK"],
        ["Trip report sent · order #4821", "09:12 · Auto", "#4C30FF", "Sent"],
      ].map(([a, b, c, tag], i) => (
        <g key={a} transform={`translate(18 ${46 + i * 34})`} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.3 + i * 0.25}s`} fill="freeze" />
          <circle cx="6" cy="10" r="4" fill={c} />
          <text x="18" y="9" fontFamily={FONT} fontSize="10.5" fontWeight="600" fill="#0E0E10">
            {a}
          </text>
          <text x="18" y="22" fontFamily={FONT} fontSize="9" fill="#6B6B73">
            {b}
          </text>
          <rect x="344" y="2" width="52" height="18" rx="9" fill={c} opacity="0.12" />
          <text x="370" y="14" textAnchor="middle" fontFamily={FONT} fontSize="8.5" fontWeight="700" fill={c}>
            {tag}
          </text>
        </g>
      ))}
    </Card>
  </Frame>
);

/* Maintenance & utilisation */
export const MaintenanceVisual = () => (
  <Frame label="Maintenance schedule and fleet utilisation" testid="visual-maintenance">
    <Card x={24} y={24} w={256} h={352}>
      <Label x={18} y={28}>UPCOMING SERVICE</Label>
      {[
        ["TRK-204 · Oil & filters", "in 420 km", 86, "#F59E0B"],
        ["VAN-17 · Brake pads", "in 1,150 km", 62, "#4C30FF"],
        ["BUS-03 · 500 h inspection", "in 36 engine h", 93, "#E5484D"],
        ["CAR-09 · Registration", "expires in 12 days", 75, "#F59E0B"],
        ["TRK-11 · Tyres", "in 3,800 km", 30, "#4C30FF"],
      ].map(([n, d, p, c], i) => (
        <g key={n} transform={`translate(18 ${52 + i * 60})`}>
          <text y="10" fontFamily={FONT} fontSize="11" fontWeight="600" fill="#0E0E10">
            {n}
          </text>
          <text x="220" y="10" textAnchor="end" fontFamily={FONT} fontSize="9.5" fontWeight="600" fill={c}>
            {d}
          </text>
          <rect y="20" width="220" height="6" rx="3" fill="#F4F4F0" />
          <rect y="20" width="0" height="6" rx="3" fill={c}>
            <animate attributeName="width" from="0" to={p * 2.2} dur="1.2s" begin={`${0.2 + i * 0.12}s`} fill="freeze" />
          </rect>
        </g>
      ))}
    </Card>
    <Card x={296} y={24} w={160} h={168}>
      <Label x={16} y={26}>UTILISATION</Label>
      <text x="16" y="70" fontFamily={DISPLAY} fontSize="32" fontWeight="700" fill="#0E0E10">
        74%
      </text>
      <text x="16" y="88" fontFamily={FONT} fontSize="9" fill="#6B6B73">
        hours driven vs available
      </text>
      {[0.9, 0.7, 0.8, 0.55, 0.85, 0.6, 0.3].map((v, i) => (
        <rect key={i} x={16 + i * 19} y={150 - v * 46} width="12" height={v * 46} rx="3" fill={i === 6 ? "#E5484D" : "#4C30FF"} opacity={i === 6 ? 1 : 0.35 + v * 0.6}>
          <animate attributeName="height" from="0" to={v * 46} dur="1s" begin={`${0.3 + i * 0.08}s`} fill="freeze" />
          <animate attributeName="y" from="150" to={150 - v * 46} dur="1s" begin={`${0.3 + i * 0.08}s`} fill="freeze" />
        </rect>
      ))}
    </Card>
    <Card x={296} y={208} w={160} h={168}>
      <Label x={16} y={26}>COST PER KM</Label>
      <text x="16" y="70" fontFamily={DISPLAY} fontSize="32" fontWeight="700" fill="#0E0E10">
        1.84
      </text>
      <text x="16" y="88" fontFamily={FONT} fontSize="9" fill="#6B6B73">
        AED · fleet average
      </text>
      {[
        ["Fuel", 0.52, "#4C30FF"],
        ["Maintenance", 0.24, "#3A1FE0"],
        ["Driver time", 0.24, "#C9C4FF"],
      ].map(([n, v, c], i) => (
        <g key={n}>
          <rect x={16 + [0, 0.52, 0.76][i] * 128} y="104" width={v * 128} height="10" rx="2" fill={c} />
          <text x="16" y={134 + i * 13} fontFamily={FONT} fontSize="9" fill="#6B6B73">
            <tspan fill={c}>■</tspan> {` ${n} · ${Math.round(v * 100)}%`}
          </text>
        </g>
      ))}
    </Card>
  </Frame>
);

/* Live alerts feed used in the problem section */
export const AlertsFeedVisual = () => (
  <Frame label="Live alerts feed catching issues before they cost money" testid="visual-alerts">
    <Card x={24} y={24} w={432} h={352}>
      <circle cx="42" cy="44" r="4" fill="#25D366">
        <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />
      </circle>
      <Label x={52} y={48}>CAUGHT IN REAL TIME · TODAY</Label>
      {[
        ["Idling 42 min · TRK-204", "Al Quoz · engine on, no movement", "≈ 9 L fuel", "#F59E0B"],
        ["After-hours ignition · VAN-03", "Sat 23:14 · outside working hours", "Misuse", "#E5484D"],
        ["Harsh braking ×3 · CAR-09", "Sheikh Zayed Rd · 112 km/h zone 100", "Safety", "#E5484D"],
        ["Temperature 9.4°C · VAN-17", "Limit 8°C · pharmacy order on board", "Cargo", "#E5484D"],
        ["Service due in 420 km · TRK-204", "Oil & filters · booked automatically", "Planned", "#4C30FF"],
        ["Left geofence · BUS-03", "Depot → Route 12 on schedule", "OK", "#16A34A"],
      ].map(([a, b, tag, c], i) => (
        <g key={a} transform={`translate(18 ${70 + i * 48})`} opacity="0">
          <animate attributeName="opacity" from="0" to="1" dur="0.5s" begin={`${0.2 + i * 0.22}s`} fill="freeze" />
          <animateTransform attributeName="transform" type="translate" from={`18 ${80 + i * 48}`} to={`18 ${70 + i * 48}`} dur="0.5s" begin={`${0.2 + i * 0.22}s`} fill="freeze" />
          <rect width="396" height="40" rx="10" fill="#FBFBF9" stroke="#EEEEE9" />
          <rect x="0" y="8" width="3" height="24" rx="1.5" fill={c} />
          <text x="14" y="17" fontFamily={FONT} fontSize="11" fontWeight="600" fill="#0E0E10">
            {a}
          </text>
          <text x="14" y="31" fontFamily={FONT} fontSize="9" fill="#6B6B73">
            {b}
          </text>
          <rect x="316" y="11" width="68" height="18" rx="9" fill={c} opacity="0.12" />
          <text x="350" y="23" textAnchor="middle" fontFamily={FONT} fontSize="8.5" fontWeight="700" fill={c}>
            {tag}
          </text>
        </g>
      ))}
    </Card>
  </Frame>
);
